import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { pushClientError } from '@/utils/clientErrorBuffer';
// DTC-F: route reminder send_at delays (1d follow-up, 2d manager
// escalation, 5d owner escalation) through the canonical clock so the
// persisted `send_at` timestamps are deterministic under
// `clock.setFixed(...)`. Delay magnitudes (24h / 2*24h / 5*24h)
// preserved verbatim.
import { clock } from '@/utils/clock';
// DMC-D3c-2: canonical deleted-state helper.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
// CSI-C1: canonical physical-location predicate. Pre-CSI-C1 the
// vehicle-present derivation in `handleTicketChange` below was an
// inline OR-chain that (a) bypassed the canonical helper from DMC-E
// and (b) silently MISSED `parking_lot === true` (a ticket parked
// in the lot without a specific spot would have leaked through as
// "not present" → appointment reminders to a customer whose vehicle
// was sitting in the lot). Routing through `isPhysicallyLocated()`
// closes both gaps simultaneously while still allowing this surface
// to layer reminder-domain operational presence (timer_status) on
// top — see the call-site comment for why the dual-rep is required.
import { isPhysicallyLocated } from '@/utils/parkingLotState';

// FIX-R132-MED-1: every reminder enqueue / cancel call previously did
// `.catch(err => console.error(...))`. Console-only errors vanish for
// shop staff who never open devtools — and reminder failures translate
// directly to skipped customer notifications and stale staff workflows.
// `logReminderInvokeError` mirrors the message to console (existing
// behaviour) AND pushes it to the sessionStorage client-error buffer so
// the next bug report carries the trace.
function logReminderInvokeError(label: string, ticket: { id: string; shop_id?: string }) {
  return (err: unknown) => {
    const errMsg =
      (err as { message?: string } | undefined)?.message
      ?? (typeof err === 'string' ? err : JSON.stringify(err ?? {}));
    console.error(`[reminder-triggers] ${label}:`, err);
    pushClientError({
      source: 'mutation',
      message: `[reminder-triggers] ${label}: ${errMsg}`,
      extra: { ticketId: ticket.id, label },
      shopId: ticket.shop_id ?? null,
    });
  };
}

interface Ticket {
  id: string;
  shop_id: string;
  // CSI-C1: `parking_lot` added so the canonical
  // `isPhysicallyLocated()` helper from DMC-E (which reads the full
  // physical-location triad: parking_lot + parking_spot_id + bay_id)
  // sees the third field. Pre-CSI-C1 the inline derivation didn't
  // read this column at all — a latent gap closed by the migration.
  parking_lot?: boolean | null;
  parking_spot_id?: string | null;
  bay_id?: string | null;
  timer_status?: string | null;
  status: string;
  customer_email?: string;
  customer_phone?: string;
  sms_opt_in?: boolean;
  email_opt_in?: boolean;
  pause_reason?: string | null;
  start_time?: string | null;
  reactivate_at?: string | null;
  picked_up_at?: string | null;
}

/**
 * Codex: Pause-reason to role routing map
 */
const PAUSE_ROLE_MAP: Record<string, { targetRole: string; timing: 'immediate' | 'delayed' }> = {
  'Customer approval': { targetRole: 'advisor', timing: 'immediate' },
  'Awaiting diagnostics': { targetRole: 'advisor', timing: 'immediate' },
  'Waiting for parts': { targetRole: 'advisor', timing: 'delayed' },
  'Blocked by other job': { targetRole: 'advisor', timing: 'immediate' },
  'Tech on break': { targetRole: 'advisor', timing: 'immediate' },
  'Other': { targetRole: 'advisor', timing: 'immediate' },
  // Stage D — the one Pause answer. Advisor needs go to the advisor; a
  // technical-help pause is NOT an advisor matter and goes to the manager.
  'Estimate needed': { targetRole: 'advisor', timing: 'immediate' },
  'Parts or pricing needed': { targetRole: 'advisor', timing: 'immediate' },
  'Advisor question': { targetRole: 'advisor', timing: 'immediate' },
  'Technical help needed': { targetRole: 'manager', timing: 'immediate' },
};

export const useReminderTriggers = (shopId: string | null) => {
  const { toast } = useToast();
  const processedTickets = useRef(new Set<string>());

  useEffect(() => {
    if (!shopId) return;

    // Enqueue reminders for new/updated tickets
    const handleTicketChange = async (ticket: Ticket) => {
      // CSI-C1: dual-representation vehicle-presence derivation.
      //  • Physical-location authority routes through DMC-E's canonical
      //    `isPhysicallyLocated(ticket)` (mirrors the SQL
      //    `sync_vehicle_present` trigger: `parking_lot=true` OR
      //    `parking_spot_id IS NOT NULL` OR `bay_id IS NOT NULL`).
      //  • Reminder-domain operational presence (timer_status running/
      //    paused) is layered on top because this surface specifically
      //    wants to suppress customer appointment reminders while a
      //    ticket is actively being worked on, even before the vehicle
      //    has been moved into a bay (e.g. walk-in service flow). The
      //    canonical helper deliberately excludes timer_status — that
      //    component is intrinsic to the reminder domain, not the
      //    parking-lot/physical-state domain.
      //  This dual shape is the established CSI pattern for a caller
      //  whose semantics are a SUPERSET of a canonical helper's contract.
      const vehiclePresent =
        isPhysicallyLocated(ticket)
        || ticket.timer_status === 'running'
        || ticket.timer_status === 'paused';

      // Codex: Dedup key includes pause_reason to prevent repeated pause saves from re-triggering
      // Core-100 R4 (CORE-AZ): the appointment instant is part of the key — a reschedule re-enqueues for
      // the new time (the server cancels reminders queued for the old one).
      const ticketKey = `${ticket.id}-${vehiclePresent}-${ticket.status}-${ticket.pause_reason || ''}-${ticket.start_time || ''}`;
      if (processedTickets.current.has(ticketKey)) return;
      processedTickets.current.add(ticketKey);

      // 1. Completed/Deleted → cancel everything, exit.
      // DMC-D3c-2: deletion check via canonical helper (both reps).
      if (ticket.status === 'Completed' || isTicketDeleted(ticket)) {
        await supabase.functions.invoke('cancel-reminders', {
          body: { ticketId: ticket.id, reason: `Ticket ${ticket.status.toLowerCase()}` },
        }).catch(logReminderInvokeError('cancel-reminders (status terminal)', ticket));
        return;
      }

      // 2. Paused → staff workflow reminders (regardless of vehicle presence)
      if (ticket.status === 'Paused') {
        const pauseReason = ticket.pause_reason || 'Other';
        const roleMapping = PAUSE_ROLE_MAP[pauseReason] || PAUSE_ROLE_MAP['Other'];

        let sendAt: string | undefined;
        if (roleMapping.timing === 'delayed' && ticket.reactivate_at) {
          sendAt = ticket.reactivate_at;
        } else if (roleMapping.timing === 'delayed') {
          sendAt = new Date(clock.nowMs() + 24 * 60 * 60 * 1000).toISOString();
        }

        // Enqueue paused_followup
        await supabase.functions.invoke('enqueue-reminders', {
          body: {
            ticketId: ticket.id, shopId: ticket.shop_id,
            type: 'paused_followup', targetRole: roleMapping.targetRole,
            ...(sendAt ? { sendAt } : {}),
          },
        }).catch(logReminderInvokeError('enqueue paused_followup', ticket));

        // Codex: Manager escalation for "Waiting for parts" after 2 days
        if (pauseReason === 'Waiting for parts') {
          supabase.functions.invoke('enqueue-reminders', {
            body: {
              ticketId: ticket.id, shopId: ticket.shop_id,
              type: 'escalation', targetRole: 'manager',
              sendAt: new Date(clock.nowMs() + 2 * 24 * 60 * 60 * 1000).toISOString(),
            },
          }).catch(logReminderInvokeError('enqueue manager escalation', ticket));
        }

        // Codex: Owner escalation for picked-up-incomplete after 5 days
        if (ticket.picked_up_at) {
          supabase.functions.invoke('enqueue-reminders', {
            body: {
              ticketId: ticket.id, shopId: ticket.shop_id,
              type: 'escalation', targetRole: 'owner',
              sendAt: new Date(clock.nowMs() + 5 * 24 * 60 * 60 * 1000).toISOString(),
            },
          }).catch(logReminderInvokeError('enqueue owner escalation', ticket));
        }

        // Cancel customer-facing appointment reminders (staff reminders preserved)
        supabase.functions.invoke('cancel-reminders', {
          body: { ticketId: ticket.id, reason: 'Ticket paused', reminderType: 'appointment' },
        }).catch(logReminderInvokeError('cancel appointment reminders (paused)', ticket));

        return;
      }

      // 3. Active + present → suppress customer appointment reminders only
      if (vehiclePresent) {
        await supabase.functions.invoke('cancel-reminders', {
          body: { ticketId: ticket.id, reason: 'Vehicle present at shop', reminderType: 'appointment' },
        }).catch(logReminderInvokeError('cancel appointment reminders (vehicle present)', ticket));
        return;
      }

      // 4. Active + not present + has contact → enqueue customer appointment reminders
      if (ticket.status === 'Active') {
        if (!ticket.customer_email && !ticket.customer_phone) return;
        if (!ticket.email_opt_in && !ticket.sms_opt_in) return;

        await supabase.functions.invoke('enqueue-reminders', {
          body: { ticketId: ticket.id, shopId: ticket.shop_id },
        }).catch(logReminderInvokeError('enqueue appointment reminders', ticket));
      }

      // Clean up old processed tickets (keep last 100)
      if (processedTickets.current.size > 100) {
        const arr = Array.from(processedTickets.current);
        processedTickets.current = new Set(arr.slice(-50));
      }
    };

    // Subscribe to ticket changes via realtime
    const channel = supabase
      .channel(`reminders-shop-${shopId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tickets',
          filter: `shop_id=eq.${shopId}`,
        },
        (payload) => {
          console.log('[reminder-triggers] New ticket:', payload.new.id);
          handleTicketChange(payload.new as Ticket);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tickets',
          filter: `shop_id=eq.${shopId}`,
        },
        (payload) => {
          const oldTicket = payload.old as Ticket;
          const newTicket = payload.new as Ticket;

          // Only process if relevant fields changed
          const vehiclePresentChanged = 
            oldTicket.parking_spot_id !== newTicket.parking_spot_id ||
            oldTicket.bay_id !== newTicket.bay_id ||
            oldTicket.timer_status !== newTicket.timer_status;
          const statusChanged = oldTicket.status !== newTicket.status;
          const contactInfoChanged = 
            oldTicket.customer_email !== newTicket.customer_email ||
            oldTicket.customer_phone !== newTicket.customer_phone ||
            oldTicket.email_opt_in !== newTicket.email_opt_in ||
            oldTicket.sms_opt_in !== newTicket.sms_opt_in;
          const pauseChanged = oldTicket.pause_reason !== newTicket.pause_reason;
          const reactivateChanged = oldTicket.reactivate_at !== newTicket.reactivate_at;
          // Core-100 R4 (CORE-AZ): a rescheduled appointment must be reminded for its NEW time
          const rescheduled = oldTicket.start_time !== undefined && oldTicket.start_time !== newTicket.start_time;

          // Codex: When reactivate_at changes, cancel old followup then re-enqueue
          if (reactivateChanged && newTicket.status === 'Paused') {
            console.log('[reminder-triggers] reactivate_at changed, re-enqueuing followup');
            // Cancel existing paused_followup first
            supabase.functions.invoke('cancel-reminders', {
              body: {
                ticketId: newTicket.id,
                reason: 'reactivate_at changed',
                reminderType: 'paused_followup',
              },
            }).catch(logReminderInvokeError('cancel stale followup', newTicket));
          }

          // Codex: When transitioning from Paused to Active (reactivation), cancel paused followups
          if (statusChanged && oldTicket.status === 'Paused' && newTicket.status === 'Active') {
            console.log('[reminder-triggers] Reactivation: canceling paused followups');
            const cancelTypes = ['paused_followup', 'picked_up_followup', 'escalation'];
            for (const reminderType of cancelTypes) {
              supabase.functions.invoke('cancel-reminders', {
                body: {
                  ticketId: newTicket.id,
                  reason: 'Ticket reactivated',
                  reminderType,
                },
              }).catch(logReminderInvokeError(`cancel ${reminderType} on reactivation`, newTicket));
            }
          }

          // Codex: Return-flow for picked-up-incomplete — when picked_up_at clears, cancel stale reminders
          const pickedUpCleared = oldTicket.picked_up_at && !newTicket.picked_up_at;
          if (pickedUpCleared) {
            console.log('[reminder-triggers] picked_up_at cleared, canceling picked-up reminders');
            for (const reminderType of ['picked_up_followup', 'escalation']) {
              supabase.functions.invoke('cancel-reminders', {
                body: {
                  ticketId: newTicket.id,
                  reason: 'Vehicle returned',
                  reminderType,
                },
              }).catch(logReminderInvokeError(`cancel ${reminderType} on return`, newTicket));
            }
          }

          if (vehiclePresentChanged || statusChanged || contactInfoChanged || pauseChanged || reactivateChanged || pickedUpCleared || rescheduled) {
            console.log('[reminder-triggers] Ticket updated:', newTicket.id, {
              vehiclePresentChanged,
              statusChanged,
              contactInfoChanged,
              pauseChanged,
              reactivateChanged,
              pickedUpCleared,
              rescheduled,
            });
            handleTicketChange(newTicket);
          }
        }
      )
      .subscribe((status, err) => {
        // Chronomancer R-RT-6: reminder triggers are time-critical
        // customer-promise signals. A silent TIMED_OUT here means new
        // tickets won't enqueue their SMS/email reminder chain until
        // the hook remounts. Surface the drop so oncall sees it.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[useReminderTriggers] realtime status:', status, err);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shopId]);

  // Manual trigger function for immediate use
  const enqueueReminders = async (ticketId: string, shopId: string) => {
    try {
      const { error } = await supabase.functions.invoke('enqueue-reminders', {
        body: { ticketId, shopId },
      });

      if (error) throw error;

      toast({
        title: 'Reminders Queued',
        description: 'Appointment reminders have been scheduled.',
      });
    } catch (error: any) {
      // FIX-R132-MED-1: also log to client-error buffer so support gets
      // a trace even if the user ignores the toast.
      logReminderInvokeError('enqueue reminders (manual)', { id: ticketId, shop_id: shopId })(error);
      toast({
        title: 'Reminder Error',
        description: 'Failed to schedule reminders. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const cancelReminders = async (ticketId: string, reason: string, reminderType?: string) => {
    try {
      const { error } = await supabase.functions.invoke('cancel-reminders', {
        body: { ticketId, reason, ...(reminderType ? { reminderType } : {}) },
      });

      if (error) throw error;

      toast({
        title: 'Reminders Canceled',
        description: 'All pending reminders have been canceled.',
      });
    } catch (error: any) {
      logReminderInvokeError('cancel reminders (manual)', { id: ticketId })(error);
      toast({
        title: 'Reminder Error',
        description: 'Failed to cancel reminders. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return {
    enqueueReminders,
    cancelReminders,
  };
};
